import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { isIssueNotFound } from '@/features/newsletter/api/errors'
import { newsletterKeys } from '@/features/newsletter/api/keys'
import {
  NEWSLETTER_PAGE_SIZE,
  newsletterService,
} from '@/features/newsletter/api/newsletterService'
import {
  NEWSLETTER_SUBSCRIPTION_AVAILABLE,
  newsletterSubscriptionService,
} from '@/features/newsletter/api/subscriptionService'
import type { NewsletterArchivePage, NewsletterIssue } from '@/schemas/newsletter'

/**
 * The newsletter query layer: three public reads, and the member's "Get new issues" switch (gated off).
 *
 * All three routes are `V5PublicAPIView`, so nothing here is gated on a
 * session and no hook takes an `enabled: isSignedIn`. A signed-out reader gets
 * the archive; that is the design, not an oversight.
 *
 * Nothing polls either. An issue is snapshotted at publish time and never
 * recomputed — `sections` is stored JSON, not a live market read — so a
 * refetch interval would move the same bytes forever. The one thing that does
 * change is WHICH issue is latest, and that changes weekly, which
 * `refetchOnWindowFocus` already covers.
 *
 * Retries are the shared client's: `ApiError.isClientError` is true for a 404,
 * so `NEWSLETTER_NOT_FOUND` fails once and lands in the not-found state
 * immediately rather than after three identical round trips.
 */

/**
 * One page of the archive.
 *
 * Factory = key + fetch only. `NewslettersScreen`'s loader `ensureRouteData`s
 * page one for the entry URL; the screen's own hook below adds
 * `keepPreviousData` so a later page click never blanks the grid — a param
 * neither `/newsletters` route (there is no `?page=`) nor a suspense query
 * could carry without re-suspending the whole page on every click (see
 * docs/route-loading.md, "Search params").
 */
export const newsletterArchiveQueryOptions = (page: number, pageSize: number) =>
  queryOptions<NewsletterArchivePage>({
    queryKey: newsletterKeys.archive(page, pageSize),
    queryFn: () => newsletterService.archive(page, pageSize),
  })

/**
 * `keepPreviousData` so the pager does not blank the grid between pages — the
 * cards the member is looking at stay until the next page arrives, which is
 * the same behaviour the Market tables' pager has.
 */
export const useNewsletterArchive = (
  page = 1,
  pageSize = NEWSLETTER_PAGE_SIZE,
): UseQueryResult<NewsletterArchivePage> =>
  useQuery({
    ...newsletterArchiveQueryOptions(page, pageSize),
    placeholderData: keepPreviousData,
  })

/**
 * The newest published issue — the hero above the archive.
 *
 * Resolves to `null`, never throws, when mm-be answers `NEWSLETTER_NOT_FOUND`
 * — nothing has ever been published, which is this route's own way of saying
 * "empty", not "broken" (`errors.ts`'s own doc). Folding that into the
 * resolved value rather than the query's error state is what lets
 * `NewslettersScreen` read this as a plain suspense query: a thrown 404 would
 * have forced either a non-suspense read (so a background failure could be
 * read back out) or the route's own error page for an outcome this app draws
 * a dedicated empty state for instead. Any OTHER failure (a 5xx, a transport
 * error) still rejects, and `NewslettersScreen.loader.ts` lets it — that is a
 * genuine "could not load", not an empty archive.
 */
export const latestNewsletterQueryOptions = () =>
  queryOptions<NewsletterIssue | null>({
    queryKey: newsletterKeys.latest(),
    queryFn: async () => {
      try {
        return await newsletterService.latest()
      } catch (error) {
        if (isIssueNotFound(error)) return null
        throw error
      }
    },
  })

/**
 * `NewslettersScreen`'s read of it — the route's loader awaits this exact
 * key, so it is always in cache by the time the screen mounts.
 */
export const useSuspenseLatestNewsletter = (): UseSuspenseQueryResult<NewsletterIssue | null> =>
  useSuspenseQuery(latestNewsletterQueryOptions())

/**
 * One issue by number — the reader page.
 *
 * Resolves to `null` on `NEWSLETTER_NOT_FOUND` for the same reason
 * `latestNewsletterQueryOptions` does: a missing, unpublished or malformed
 * issue number is this reader's not-found state, not a route failure, and
 * folding it into the resolved value is what lets `NewsletterIssueScreen`
 * read this as a plain suspense query — the same shape `companyDetailQueryOptions`
 * uses for Stock's own "not found" (`data === null`).
 */
export const newsletterIssueQueryOptions = (issueNumber: number) =>
  queryOptions<NewsletterIssue | null>({
    queryKey: newsletterKeys.issue(issueNumber),
    queryFn: async () => {
      try {
        return await newsletterService.issue(issueNumber)
      } catch (error) {
        if (isIssueNotFound(error)) return null
        throw error
      }
    },
  })

/**
 * `NewsletterIssueScreen`'s read of it — the route's loader awaits this exact
 * key for the URL's issue number, so it is always in cache by the time the
 * screen mounts (see `NewsletterIssueScreen.loader.ts`).
 */
export const useSuspenseNewsletterIssue = (
  issueNumber: number,
): UseSuspenseQueryResult<NewsletterIssue | null> =>
  useSuspenseQuery(newsletterIssueQueryOptions(issueNumber))

/** The member's "Get new issues" switch. Gated: with no endpoint (`NEWSLETTER_SUBSCRIPTION_AVAILABLE`) nothing is asked. */
export const useNewsletterSubscription = (): UseQueryResult<boolean> =>
  useQuery({
    queryKey: newsletterKeys.subscription(),
    queryFn: () => newsletterSubscriptionService.get(),
    enabled: NEWSLETTER_SUBSCRIPTION_AVAILABLE,
  })

/** Flips the switch. The new value shows at once and is put back if the server refuses it. */
export const useSetNewsletterSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (subscribed: boolean) => newsletterSubscriptionService.set(subscribed),
    onMutate: async (subscribed) => {
      await queryClient.cancelQueries({ queryKey: newsletterKeys.subscription() })
      const previous = queryClient.getQueryData<boolean>(newsletterKeys.subscription())
      queryClient.setQueryData(newsletterKeys.subscription(), subscribed)
      return { previous }
    },
    onError: (_error, _subscribed, context) => {
      queryClient.setQueryData(newsletterKeys.subscription(), context?.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: newsletterKeys.subscription() }),
  })
}
